import type { FilterKey, StackId } from '@arrow/game-engine';
import type { Level } from '@arrow/puzzle-schema';

import { apiUrl, isPermanentFailure, postJson, type WriteResult } from '../api/client.ts';
import type { KeyValueStore } from '../storage/kv.ts';
import { APP_VERSION, browserBeacon, isInternalDevice, platformKeys, platformStore, usageCountsOn } from './config.ts';
import { getDeviceId } from './identity.ts';

/**
 * First-party analytics: events queue in localStorage and go to `POST /v1/events` in
 * batches. It never throws and never blocks play: `track` only appends to the queue;
 * delivery happens on a timer and when the page hides (via `navigator.sendBeacon`).
 */

/** The api's allowlist (apps/api/src/events/event-names.ts). Anything else is a 400. */
export const EVENT_NAMES = [
	'app_opened',
	'daily_opened',
	'daily_started',
	'daily_completed',
	'share_pressed',
	'shared_link_opened',
	'practice_started',
	'practice_completed',
	'stats_viewed',
	'challenge_created',
	'challenge_opened',
	'challenge_started',
	'challenge_completed',
	'rematch_sent',
	'install_prompt_shown',
	'install_prompt_accepted',
	'install_prompt_dismissed',
	'installed',
	'opened_standalone',
	'menu_opened',
	'settings_changed',
	'how_to_play_opened',
	'stats_control_used',
	'play_viewed',
	'stack_opened',
	'stack_played',
	'stack_saved',
	'stack_deleted',
	'archive_searched',
	'archive_no_results',
	'archive_relaxed',
	'notify_ask_shown',
	'notify_ask_tapped',
	'notify_ask_dismissed',
	'notify_prompt_result',
	'notify_test_sent',
	'notify_pref_changed',
	'notify_state',
] as const;
export type EventName = (typeof EVENT_NAMES)[number];

/** How a share went (`shareResult`). */
export type ShareOutcome = 'native' | 'clipboard' | 'cancelled' | 'failed';

/** Props every daily event carries. `isToday`: the daily is today's (local date), not an archive day. */
export interface DailyProps {
	number: number;
	level: Level;
	isToday: boolean;
}

/** The props each event takes (docs/DAILY-LOOP-PLAN.md §8). */
export interface EventProps {
	app_opened: { route: string; isReturning: boolean; daysSinceFirstSeen: number; display?: 'browser' | 'standalone' };
	/** `daysAgo`: how many days before today the daily is (0 for today's). */
	daily_opened: DailyProps & { via: 'today' | 'link' | 'share' | 'challenge' | 'stack' | 'search' | 'lookup' | 'next'; daysAgo: number };
	daily_started: DailyProps;
	daily_completed: DailyProps & {
		durationMs: number;
		checks: number;
		reveals: number;
		mistakes: number;
		bestFlow: number;
		/** Completed on its own local day (counts for the streak). */
		onTime: boolean;
	};
	share_pressed: {
		number: number | null;
		level?: Level;
		method: ShareOutcome;
		kind?: 'daily' | 'practice';
		/** Which share: the dare (`/c/:code`), the plain result, or a rematch from a challenge. */
		variant?: ShareVariant;
		/** One id per press, so a share can be followed to its opens. */
		shareId?: string;
	};
	shared_link_opened: { number?: number; level?: Level };
	practice_started: { puzzleId: string; level?: Level };
	practice_completed: {
		puzzleId: string;
		level?: Level;
		durationMs?: number;
		checks?: number;
		reveals?: number;
		mistakes?: number;
		bestFlow?: number;
	};
	stats_viewed: Record<string, never>;
	challenge_created: ChallengeProps;
	challenge_opened: ChallengeProps & { alreadySolved: boolean };
	challenge_started: ChallengeProps;
	challenge_completed: ChallengeProps & { beat: boolean; durationMs: number; marginMs: number };
	rematch_sent: ChallengeProps & { method: ShareOutcome; rematchCode: string | null };
	/** An install invitation came on screen (once per surface per day). */
	install_prompt_shown: { surface: InstallSurface; platform: InstallPlatform; timesShown: number };
	/** `cta` tapped our button; `native` Chrome's sheet came back accepted; `guide_done` Done in the iPhone guide; `way_out` Open in Safari / Chrome. */
	install_prompt_accepted: { surface: InstallSurface; platform: InstallPlatform; step: 'cta' | 'native' | 'guide_done' | 'way_out' };
	/** `count`: how many times it's been turned down on this device, this one included. */
	install_prompt_dismissed: {
		surface: InstallSurface;
		platform: InstallPlatform;
		via: 'not_now' | 'native_cancel' | 'guide_closed' | 'didnt_work';
		count: number;
	};
	installed: { platform: InstallPlatform; via: 'appinstalled' | 'first_standalone_open' };
	opened_standalone: { platform: InstallPlatform; daysSinceInstall: number };
	/** The ≡ menu sheet opened, and on which screen. */
	menu_opened: { screen: MenuScreen };
	/** A Settings switch flipped, or the theme chosen (`value` is the new setting). */
	settings_changed: { key: 'haptics' | 'sound' | 'clock' | 'largeClues' | 'shareBoard'; value: boolean } | { key: 'theme'; value: 'system' | 'light' | 'dark' };
	/** How to play opened, from the menu on this screen. */
	how_to_play_opened: { from: MenuScreen };
	/** A control on Stats used: a level card, a month step (‹ ›), or Every time opened. */
	stats_control_used: { control: 'level' | 'month' | 'times' };
	/** Play opened (once per visit to the page). */
	play_viewed: { newPlayer: boolean };
	/** A stack opened (a tile, the rail). A player-made stack is `custom` (never its id or name). */
	stack_opened: { stack: StackEventId; custom?: true };
	/** A puzzle played from a stack: ▶, "Play …", or a row. `position` is 1-based; `daysAgo` dailies only. */
	stack_played: { stack: StackEventId; position: number; daysAgo?: number; custom?: true };
	/** A player-made stack saved (new, or `edited`): the rules it uses by name, never their values or its name. */
	stack_saved: { rules: Array<FilterKey | 'sort'>; custom: true; edited: boolean };
	/** A player-made stack deleted. */
	stack_deleted: { custom: true };
	/** A search settled. Never the text: what it read as, how many it found, how many words it didn't know. */
	archive_searched: { kind: 'number' | 'date' | 'words'; results: number; unknownWords: number };
	archive_no_results: { kind: 'number' | 'date' | 'words' };
	/** A no-results loosening taken: the filter dropped. */
	archive_relaxed: { key: FilterKey };
	/** Notifications (docs/notifications/contract.md, "Analytics"). An ask (a moment, or the sheet) came on screen. */
	notify_ask_shown: { surface: NotifySurface };
	/** A moment's yes ("Remind me", "Turn on"): it opens the sheet. */
	notify_ask_tapped: { surface: NotifySurface };
	/** "Not now", or the sheet closed without turning them on. */
	notify_ask_dismissed: { surface: NotifySurface };
	/** What the browser's prompt (from "Turn on notifications") came back as. */
	notify_prompt_result: { surface: NotifySurface; result: 'granted' | 'denied' | 'dismissed' };
	notify_test_sent: Record<string, never>;
	/** A pref changed once on: a switch (`on`) or a time (no value: no times leave the device here). */
	notify_pref_changed: { key: NotifyPrefKey; on?: boolean };
	/** This device's notification state, once a local day. */
	notify_state: { state: 'unsupported' | 'needs_home' | 'off' | 'blocked' | 'on' | 'stopped' };
}

/** Where a notification ask was (contract, `surface`). */
export type NotifySurface = 'bell' | 'first_solve' | 'streak_solve' | 'nest_join' | 'nudge_sent' | 'streak_card' | 'settings';
/** The push prefs' field names (contract, "Preferences"). */
export type NotifyPrefKey =
	| 'morning'
	| 'morningAt'
	| 'streakSaver'
	| 'streakSaverAt'
	| 'roundUps'
	| 'nudges'
	| 'sundayTable'
	| 'beatTime'
	| 'quietFrom'
	| 'quietTo';

/** A stack as analytics names it: a built-in's id, or `custom` for one the player made. */
export type StackEventId = StackId | 'custom';

/** Where the ≡ menu was opened. */
export type MenuScreen = 'today' | 'play' | 'nest' | 'stats' | 'game' | 'challenge' | 'privacy' | 'invite';

/** Where an install invitation was (docs/mockups/install.html, Spec panel 3). */
export type InstallSurface = 'today_card' | 'menu' | 'stats_row' | 'desktop_button' | 'notifications';
/** The platform prop on every install event. */
export type InstallPlatform = 'android' | 'ios_safari' | 'ios_other' | 'in_app' | 'desktop';

/** The three share buttons: Challenge a friend, Share result only, Send the rematch. */
export type ShareVariant = 'challenge' | 'result' | 'rematch';

/** Props on every challenge event. */
export interface ChallengeProps {
	code: string;
	number: number;
	level: Level;
}

export interface QueuedEvent {
	name: EventName;
	/** ISO 8601, client clock. */
	at: string;
	props?: Record<string, unknown>;
}

/** Oldest events are dropped past this, so an offline device can't fill storage. */
export const MAX_QUEUE = 500;
/** The api takes 1–50 events per request. */
export const MAX_BATCH = 50;
/** The api refuses props bigger than this (as JSON). */
export const MAX_PROPS_BYTES = 2048;
export const FLUSH_INTERVAL_MS = 15_000;
/** At most this many batches per timed flush (the api allows 60 requests a minute per device). */
const MAX_BATCHES_PER_FLUSH = 4;

const NAMES = new Set<string>(EVENT_NAMES);

function isQueued(value: unknown): value is QueuedEvent {
	if (typeof value !== 'object' || value === null) return false;
	const v = value as Record<string, unknown>;
	return (
		typeof v.name === 'string' &&
		NAMES.has(v.name) &&
		typeof v.at === 'string' &&
		(v.props === undefined || (typeof v.props === 'object' && v.props !== null && !Array.isArray(v.props)))
	);
}

export interface AnalyticsDeps {
	store: KeyValueStore;
	enabled: () => boolean;
	deviceId: () => string;
	/** An internal device (`?internal=1`): every batch says so, and the reports leave it out. */
	internal?: () => boolean;
	now: () => number;
	appVersion: string;
	post: (body: unknown, keepalive: boolean) => Promise<WriteResult>;
	/** `navigator.sendBeacon` bound, or null where there isn't one. */
	beacon: ((url: string, data: Blob) => boolean) | null;
	eventsUrl: string;
}

export interface Analytics {
	/** Queue an event. Never throws; a no-op when analytics is off. */
	track<N extends EventName>(name: N, props?: EventProps[N]): void;
	/** Send queued events with fetch (up to a few batches). Resolves when done; never rejects. */
	flush(): Promise<void>;
	/** Send everything queued with sendBeacon now (page hide). Falls back to keepalive fetch. */
	flushOnHide(): void;
	/** Events waiting to be sent. */
	pending(): QueuedEvent[];
	/** Start the timer and the hide listeners (idempotent; browser only). */
	start(): void;
	stop(): void;
}

export function createAnalytics(deps: AnalyticsDeps): Analytics {
	/** Used only when storage refuses writes, so a session still delivers its events. */
	let memory: QueuedEvent[] | null = null;
	let sending = false;
	let timer: ReturnType<typeof setInterval> | null = null;
	const listeners: Array<() => void> = [];

	const read = (): QueuedEvent[] => {
		if (memory) return memory;
		const raw = deps.store.get(platformKeys.events);
		if (raw === null) return [];
		try {
			const parsed = JSON.parse(raw) as unknown;
			return Array.isArray(parsed) ? parsed.filter(isQueued).slice(-MAX_QUEUE) : [];
		} catch {
			return [];
		}
	};
	const write = (queue: QueuedEvent[]) => {
		const trimmed = queue.slice(-MAX_QUEUE);
		if (memory) {
			memory = trimmed;
			return;
		}
		if (trimmed.length === 0) deps.store.remove(platformKeys.events);
		else if (!deps.store.set(platformKeys.events, JSON.stringify(trimmed))) memory = trimmed;
	};
	/** Takes the oldest `n` events off the queue (they're re-queued if sending fails). */
	const take = (n: number): QueuedEvent[] => {
		const queue = read();
		const batch = queue.slice(0, n);
		write(queue.slice(n));
		return batch;
	};
	const putBack = (batch: QueuedEvent[]) => write([...batch, ...read()]);
	const body = (events: QueuedEvent[]) => ({
		deviceId: deps.deviceId(),
		appVersion: deps.appVersion,
		...(deps.internal?.() ? { internal: true } : {}),
		events,
	});

	const analytics: Analytics = {
		track(name, props) {
			try {
				if (!NAMES.has(name) || !deps.enabled()) return;
				const event: QueuedEvent = { name, at: new Date(deps.now()).toISOString() };
				if (props && Object.keys(props).length > 0) {
					const json = JSON.stringify(props);
					// Oversized props would get the whole batch refused: keep the event, drop the props.
					if (new TextEncoder().encode(json).length <= MAX_PROPS_BYTES) event.props = JSON.parse(json) as Record<string, unknown>;
				}
				write([...read(), event]);
			} catch {
				// Analytics never breaks play.
			}
		},

		async flush() {
			if (sending) return;
			sending = true;
			try {
				for (let i = 0; i < MAX_BATCHES_PER_FLUSH; i++) {
					if (!deps.enabled()) return;
					const batch = take(MAX_BATCH);
					if (batch.length === 0) return;
					const result = await deps.post(body(batch), false).catch(() => ({ ok: false, status: 0 }));
					if (result.ok) continue;
					// A batch the api refuses as invalid would be refused forever: drop it.
					if (!isPermanentFailure(result)) putBack(batch);
					return;
				}
			} catch {
				// Never rejects.
			} finally {
				sending = false;
			}
		},

		flushOnHide() {
			try {
				if (!deps.enabled()) return;
				for (let i = 0; i < Math.ceil(MAX_QUEUE / MAX_BATCH); i++) {
					const batch = take(MAX_BATCH);
					if (batch.length === 0) return;
					const json = JSON.stringify(body(batch));
					// text/plain: a beacon can't send application/json without a CORS preflight.
					const sent = deps.beacon?.(deps.eventsUrl, new Blob([json], { type: 'text/plain' })) ?? false;
					if (sent) continue;
					void deps
						.post(body(batch), true)
						.then((r) => {
							if (!r.ok && !isPermanentFailure(r)) putBack(batch);
						})
						.catch(() => putBack(batch));
					return;
				}
			} catch {
				// Never throws.
			}
		},

		pending: () => read(),

		start() {
			if (timer !== null || typeof window === 'undefined') return;
			timer = setInterval(() => void analytics.flush(), FLUSH_INTERVAL_MS);
			const onVisibility = () => {
				if (document.visibilityState === 'hidden') analytics.flushOnHide();
			};
			const onHide = () => analytics.flushOnHide();
			document.addEventListener('visibilitychange', onVisibility);
			window.addEventListener('pagehide', onHide);
			listeners.push(
				() => document.removeEventListener('visibilitychange', onVisibility),
				() => window.removeEventListener('pagehide', onHide),
			);
		},

		stop() {
			if (timer !== null) clearInterval(timer);
			timer = null;
			for (const off of listeners.splice(0)) off();
		},
	};
	return analytics;
}

/** The app's analytics: localStorage queue, the real api, starts itself on first use. */
export const analytics: Analytics = createAnalytics({
	store: platformStore,
	enabled: () => usageCountsOn(platformStore),
	deviceId: () => getDeviceId(platformStore),
	internal: () => isInternalDevice(platformStore),
	now: () => Date.now(),
	appVersion: APP_VERSION,
	post: (body, keepalive) => postJson('/v1/events', body, { keepalive }),
	beacon: browserBeacon(),
	eventsUrl: apiUrl('/v1/events'),
});

/**
 * Record an analytics event (see `EventProps` for each event's props). Never throws,
 * never blocks: it queues, and delivery happens in the background.
 */
export function track<N extends EventName>(name: N, props?: EventProps[N]): void {
	if (!usageCountsOn(platformStore)) return;
	analytics.start();
	analytics.track(name, props);
}

/** Send what's queued now (e.g. after an important event). Never rejects. */
export function flushAnalytics(): Promise<void> {
	return analytics.flush();
}
